import { GlobalRules } from '@/components/admin/reglages/global-rules';
import { GuardrailList } from '@/components/admin/reglages/guardrail-list';
import { VersionedEditor } from '@/components/admin/versioned/versioned-editor';
import { ruleSettingsApi } from '@/lib/api';
import type { RuleSettingsDraft } from '@/lib/rule-settings-draft';
import { RULE_SETTINGS_DRAFT_KIT } from '@/lib/rule-settings-draft';
import { RULE_SETTINGS_WORDING } from '@/lib/rule-settings-wording';
import type {
  RuleSettingsContent,
  RuleSettingsDetail,
  RuleSettingsDraftContent,
  RuleSettingsVersion,
  VersionSummary,
} from '@matter_extractor/shared';

/**
 * The editor of « Les règles » (vitrine.md section 5, issue #192): the pattern of
 * §4 in its `settings` logement, plus the two lists.
 *
 * Everything the other editors do identically - the head, the history, the save
 * box and « Passer en production », the debounced autosave and its optimistic
 * control, undo and redo - is `VersionedEditor`. What is here is what only the
 * rules know: their API, their draft kit, their wording and the two lists, which
 * became the DRAFT: a rule typed, a guardrail moved are an autosave, and
 * « Enregistrer une nouvelle version » is the one write, for both lists at once -
 * where the screen used to have a save for the rules and one per guardrail.
 *
 * ## The cache keys
 *
 * `['rule-settings', email, RULE_SETTINGS_ID]` holds the detail and the layer
 * writes every answer into it. There is no list to invalidate - a singleton has
 * none - so the second prefix names one nobody reads, rather than the detail's
 * own, which a prefix invalidation would refetch after every autosave.
 */

/** The prefix of the detail's query key, shared with the route that reads it. */
export const RULE_SETTINGS_QUERY_KEY = 'rule-settings';

export interface RuleSettingsEditorProps {
  readonly detail: RuleSettingsDetail;
  /** A version opened read-only by « Voir », or null. */
  readonly viewing: RuleSettingsVersion | null;
  readonly onCloseView: () => void;
  readonly onView: (version: VersionSummary) => void;
  /** Re-read the rules from the server - what the stale-draft box offers. */
  readonly onReload: () => void;
}

export function RuleSettingsEditor({
  detail,
  viewing,
  onCloseView,
  onView,
  onReload,
}: RuleSettingsEditorProps) {
  return (
    <VersionedEditor<RuleSettingsContent, RuleSettingsDraftContent, RuleSettingsDraft>
      wording={RULE_SETTINGS_WORDING}
      api={ruleSettingsApi}
      kit={RULE_SETTINGS_DRAFT_KIT}
      keys={{ detail: RULE_SETTINGS_QUERY_KEY, list: 'rule-settings-sans-liste' }}
      detail={detail}
      viewing={viewing}
      onCloseView={onCloseView}
      onView={onView}
      onReload={onReload}
      onCreated={() => undefined}
      layout="settings"
      renderFields={({ draft, readOnly, onDraft }) => (
        <div className="space-y-10">
          <GlobalRules
            rules={draft.rules}
            readOnly={readOnly}
            onChange={(rules) => onDraft({ ...draft, rules })}
          />
          <GuardrailList
            guardrails={draft.guardrails}
            readOnly={readOnly}
            onChange={(guardrails) => onDraft({ ...draft, guardrails })}
          />
        </div>
      )}
    />
  );
}
